// medplum-client.js — the ONE real Medplum session per browser for every
// ONLINE-PRIMARY staff screen (queue, reception, patients, schedule, reports…).
//
// SINGLE-SESSION MODEL (the cutover): the browser holds exactly one logged-in
// Medplum session, started by Login.jsx via loginWithPassword() and restored
// from localStorage (prefix 'medplum.user.') on later page loads. That one
// session drives BOTH the login gate and every FHIR read/write — there is no
// per-identity auto-login any more. The demo email/password entries in
// /assets/medplum-demo.json remain for NODE HARNESSES only; the app never
// reads them to log in, and the cfg object handed to screens is stripped of
// them (see appBundle) so a straggler cfg.currentUser reader fails loud
// instead of silently impersonating the demo doctor.
//
// MedplumClient persists its session to window.localStorage and refreshes the
// access token from the refresh token before it expires (refreshIfExpired,
// called internally on every request). When the refresh token itself is dead,
// the client clears its active login, getProfileAsync() resolves undefined,
// and getMedplum() throws NotLoggedInError — App.jsx turns that into a /login
// redirect (no retry loops).
//
// currentUser is DERIVED from live Medplum resources (deriveCurrentUser), not
// from static demo JSON: role from the server-provided ProjectMembership
// (auth/me) + PractitionerRole, department from the dept Organization's
// department-slug identifier. Cached per profile; recomputed on login.
import { MedplumClient } from '@medplum/core';
import { setClinicTimezone } from './clinic-time.js';
import { setServerBase } from './net-base.js';
import { navTo } from './opd-nav.js';
import { isNetworkError } from './net-error.js';

const STORAGE_PREFIX = 'medplum.user.';
const DEPT_SLUG_SYSTEM = 'https://healther.io/opd/department-slug';
const MUST_CHANGE_PWD_EXT = 'https://healther.io/opd/must-change-password';
// Seniority coding on the caller's OWN PractitionerRole.code (written by the
// admin-staff-invite Bot). Code 'hod' = Head of Department — the seniority the
// Protocols management UI + the manage-protocol write-auth gate on.
const SENIORITY_SYSTEM = 'https://healther.io/opd/seniority';
// The sibling GRADE coding (1|2|3) that refines the JR/SR bands into the fine
// 8-tier designation ladder (JR1…SR3 + Associate Professor + HOD). See
// medplum-designation.js for the full model.
const SENIORITY_GRADE_SYSTEM = 'https://healther.io/opd/seniority-grade';

// Typed auth failure: "there is no live Medplum session". App.jsx catches this
// (instanceof) on staff routes and redirects to /login preserving state.from.
export class NotLoggedInError extends Error {
  constructor(message = 'Not logged in to Medplum') {
    super(message);
    this.name = 'NotLoggedInError';
  }
}

/**
 * Pure classifier for a failed/empty session resume: is the stored session merely
 * UNREACHABLE right now (offline — keep working from the cache) or genuinely GONE
 * (route to /login)? Every environment fact is passed in so this stays pure and
 * unit-testable — it is the ONE place the offline-vs-expired policy lives.
 *
 * Why this matters: a NETWORK failure while a login is still on disk means "can't
 * reach /auth/me right now", NOT "logged out". A shared clinic tablet that drops
 * wifi must keep working from cache + outbox, not bounce every user to a login
 * screen it also can't reach. Only a definitively dead session (no stored login,
 * or a server-REJECTED refresh) is 'unauth'.
 *
 * @param {{ networkFailure: boolean, hasStoredLogin: boolean }} ctx
 * @returns {'offline' | 'unauth'} 'offline' → keep the session, render from cache;
 *   'unauth' → the session is gone, go to /login.
 */
export function classifySessionResume({ networkFailure = false, hasStoredLogin = false } = {}) {
  return networkFailure && hasStoredLogin ? 'offline' : 'unauth';
}

// The @medplum/core `onUnauthenticated` callback (wired into THE staff client in
// getClient below). The library invokes it ONLY on a server-REJECTED token refresh
// — true auth death: the refresh token itself is dead/revoked — or a 401 with no
// refresh possible. A NETWORK failure during a refresh never reaches here (the
// client rethrows WITHOUT clearing the login and WITHOUT calling this), so this
// fires for genuine expiry only, never for offline (verified in @medplum/core
// client.ts: fetchTokens catch → rethrow vs !response.ok → clearActiveLogin +
// onUnauthenticated). It routes to /login preserving where the user was (?next=),
// and is IDEMPOTENT: many concurrent 401s collapse to ONE redirect. The latch
// resets on the full-page load into /login (and if we are already on /login).
let _unauthRedirected = false;
export function _resetUnauthLatchForTest() { _unauthRedirected = false; } // test-only
export function onMedplumUnauthenticated() {
  if (_unauthRedirected) return;
  _unauthRedirected = true;
  currentUserCache = null; // drop derived role/department for the dead session
  let here = '/';
  try { here = window.location.pathname + window.location.search; } catch (_) {}
  if (here.startsWith('/login')) { _unauthRedirected = false; return; } // already there
  const next = '?next=' + encodeURIComponent(here);
  try {
    navTo('/login' + next, { replace: true });
  } catch (_) {
    try { window.location.replace('/login' + next); } catch (__) {}
  }
}

// Namespaces every localStorage key MedplumClient touches, so the staff
// session and the anonymous device sessions never clobber each other
// (MedplumClient's default storage uses one fixed key).
class PrefixedStorage {
  constructor(prefix) { this.prefix = prefix; }
  getString(key) { return window.localStorage.getItem(this.prefix + key) ?? undefined; }
  setString(key, value) {
    if (value === undefined) window.localStorage.removeItem(this.prefix + key);
    else window.localStorage.setItem(this.prefix + key, value);
  }
  getObject(key) { const s = this.getString(key); return s === undefined ? undefined : JSON.parse(s); }
  setObject(key, value) { this.setString(key, value === undefined ? undefined : JSON.stringify(value)); }
  clear() {
    for (const k of Object.keys(window.localStorage)) {
      if (k.startsWith(this.prefix)) window.localStorage.removeItem(k);
    }
  }
}

let configPromise = null;

function loadConfig() {
  if (!configPromise) {
    configPromise = fetch('/assets/medplum-demo.json', { cache: 'no-store' }).then((res) => {
      if (!res.ok) throw new Error('medplum-demo config missing (' + res.status + ')');
      return res.json();
    });
  }
  return configPromise;
}

// ── THE staff session ───────────────────────────────────────────────────────
let clientPromise = null;   // the ONE MedplumClient (constructed once per page)
let bundlePromise = null;   // sanitized { cfg, hospital } app config (identity-free)

function getClient() {
  if (!clientPromise) {
    clientPromise = loadConfig().then((raw) =>
      new MedplumClient({
        baseUrl: raw.baseUrl,
        storage: new PrefixedStorage(STORAGE_PREFIX),
        // OPTIONAL dedicated tablet ClientApplication. When the deploy config
        // carries a `clientId`, this session is governed by that ClientApplication
        // — its `refreshTokenLifetime` becomes the clinic-tablet session length
        // (the per-client lever; see scripts/medplum/provision-tablet-client.mjs).
        // Absent → '' (library default) → the Medplum server's default refresh
        // lifetime (2 weeks). MedplumClient threads this clientId into both the
        // login request and every refresh (client.ts startLogin / runRefreshWithLock).
        clientId: raw.clientId || undefined,
        // True auth death only: a server-REJECTED refresh routes to /login. Offline
        // never reaches this (see onMedplumUnauthenticated).
        onUnauthenticated: onMedplumUnauthenticated,
      }));
  }
  return clientPromise;
}

// The app-visible config is public deployment metadata only. In particular,
// anon ClientApplication credentials are never bundle configuration: a browser
// secret is public, and Medplum also archives every direct Bot input that such
// a client submits. Anonymous flows terminate credentials at the existing
// same-origin gateways and receive only non-secret hospital/Bot metadata here.
function appBundle() {
  if (!bundlePromise) {
    bundlePromise = loadConfig().then((raw) => {
      const cfg = {};
      for (const [k, v] of Object.entries(raw)) {
        if (k === 'email' || k === 'password' || k === 'currentUser' || k === 'anonClients') continue;
        if (v && typeof v === 'object' && !Array.isArray(v) && ('password' in v || 'currentUser' in v)) continue;
        cfg[k] = v;
      }
      // ONE config point for clinic-local time across every screen: the clinic
      // IANA timezone (default Asia/Kolkata) drives all wall-clock <-> instant
      // conversion in lib/clinic-time.js. Set it here — the single choke point
      // every screen's getMedplum()/appBundle() passes through.
      setClinicTimezone(raw.hospital && raw.hospital.timezone);
      // Plumb the app's OWN-server base (Express /api, /uploads, /assets, WS) into
      // net-base. Absent on web (same-origin) → net-base stays the identity fn;
      // present on the Capacitor build → those legs become absolute. This is the
      // one config choke point every screen already passes through.
      setServerBase(raw.serverBase);
      return { cfg, hospital: raw.hospital };
    });
  }
  return bundlePromise;
}

/** Public, identity-free application configuration for anonymous boot screens.
 * This deliberately does not construct or resume any Medplum session. */
export async function getPublicAppConfig() {
  const { cfg } = await appBundle();
  return cfg;
}

/**
 * Staff login — the ONE real Medplum password login (Login.jsx). PKCE is
 * auto-injected by @medplum/core (startLogin → ensureCodeChallenge;
 * processCode sends the verifier). On success the session is persisted under
 * 'medplum.user.*' and the derived-currentUser cache is reset for the new
 * principal. Returns the same session bundle getMedplum() resolves to.
 */
export async function loginWithPassword({ email, password }) {
  const raw = await loadConfig();
  const projectId = typeof raw.projectId === 'string' ? raw.projectId.trim() : '';
  if (!projectId) {
    throw new Error('Medplum login configuration is missing projectId (/assets/medplum-demo.json)');
  }
  const medplum = await getClient();
  // `offline_access` is what makes the server issue a REFRESH token. Without it
  // (the old call) the server defaulted the scope to bare `openid`, issued NO
  // refresh token, and every session hard-died at the 1-hour access-token
  // lifetime — the shared-tablet expiry bug. `offline_access` is the OAuth-
  // standard scope Medplum honours (server oauth/utils.ts includeRefreshToken:
  // `scopeArray.includes('offline_access')`; the deprecated `offline` also works
  // but warns). A dedicated tablet ClientApplication with `refresh_token` in its
  // grantType would also trigger it, but requesting the scope keeps refresh
  // tokens flowing even on the default (no-clientId) config.
  const login = await medplum.startLogin({
    email,
    password,
    projectId,
    scope: 'openid offline_access',
  });
  if (!login.code) {
    // Multiple project memberships would need a pick-project step we do not
    // have (one hospital = one project). Fail loud rather than guess.
    throw new Error(login.memberships
      ? 'This account belongs to multiple Medplum projects — project selection is not supported'
      : 'Medplum login did not return an authorization code');
  }
  await medplum.processCode(login.code);
  currentUserCache = null; // new principal → rederive role/department
  return getMedplum();
}

/**
 * Returns { medplum, cfg, hospital } for THE logged-in staff session, restored
 * from storage if needed — or throws NotLoggedInError when there is no live
 * session (never logged in, or the refresh token expired and the client
 * cleared itself). The profile check runs on EVERY call (cheap after init) so
 * an expired session surfaces NotLoggedInError at the next navigation.
 *
 * The identity argument is accepted and IGNORED: pre-cutover callers pass
 * 'admin'/'receptionist' etc., but there is only one session now — the
 * logged-in user either has the breadth or the App.jsx role gate bounces them.
 */
export async function getMedplum(_identity) {
  const medplum = await getClient();
  // getProfile() races the constructor's fire-and-forget session resume (it
  // sets the access token synchronously but fetches /auth/me async) — always
  // undefined immediately after construction even when a valid session is
  // restored from localStorage. getProfileAsync() awaits that resume first.
  //
  // OFFLINE ≠ EXPIRED: on a cold OFFLINE boot the resume's /auth/me fetch
  // REJECTS with a network error (or, with no stored login, resolves undefined).
  // A stored login + a network failure means the session is intact and simply
  // unreachable — we must keep working from the SWR cache, NOT throw
  // NotLoggedInError (which App.jsx would turn into a /login redirect the tablet
  // can't even reach). classifySessionResume() is the single discriminator.
  let profile;
  try {
    profile = await medplum.getProfileAsync();
  } catch (err) {
    const hasStoredLogin = !!activeLoginProfileId();
    if (classifySessionResume({ networkFailure: isNetworkError(err), hasStoredLogin }) === 'offline') {
      const { cfg, hospital } = await appBundle();
      return { medplum, cfg, hospital }; // offline: keep the session, reads hit cache
    }
    // Not a keep-working-offline case. If the stored login is gone, the client
    // cleared it on a server-rejected refresh (true auth death) → NotLoggedInError
    // routes to /login (onUnauthenticated has usually already fired the redirect;
    // this keeps the loader path consistent). Session still on disk + a
    // non-network error is a real fault → fail loud.
    if (!hasStoredLogin) throw new NotLoggedInError();
    throw err;
  }
  if (!profile) {
    const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
    const hasStoredLogin = !!activeLoginProfileId();
    if (classifySessionResume({ networkFailure: offline, hasStoredLogin }) === 'offline') {
      const { cfg, hospital } = await appBundle();
      return { medplum, cfg, hospital };
    }
    throw new NotLoggedInError();
  }
  const { cfg, hospital } = await appBundle();
  return { medplum, cfg, hospital };
}

/**
 * The logged-in user's Medplum profile id, resolved SYNCHRONOUSLY from the
 * session restored in localStorage — with NO network call. On a cold OFFLINE
 * launch @medplum/core has the active login (access token + profile reference)
 * from storage, but `getProfile()` stays undefined until the /auth/me resume
 * succeeds (which it can't offline). The stored LoginState still carries
 * `profile.reference` ('Practitioner/<id>'), so this returns `<id>` offline. Used
 * by swrLoad to key the per-user SWR cache the SAME way online and offline —
 * otherwise a cold offline read keys under 'anon', misses the persisted rows, and
 * the offline loader throws "Failed to fetch". Returns null when no session is
 * stored (genuinely logged out). Reads the SAME 'medplum.user.activeLogin' key
 * MedplumClient persists (see authHeader()).
 */
export function activeLoginProfileId() {
  let raw;
  try {
    raw = window.localStorage.getItem(STORAGE_PREFIX + 'activeLogin');
  } catch (_) {
    return null; // storage unavailable (privacy mode)
  }
  if (!raw) return null;
  try {
    const ref = JSON.parse(raw).profile && JSON.parse(raw).profile.reference;
    return ref ? ref.split('/').pop() : null; // 'Practitioner/<id>' → '<id>'
  } catch (_) {
    return null; // corrupt entry — treat as no session
  }
}

/**
 * Sign out: revoke the session server-side (medplum.signOut → POST
 * auth/logout + clear its own storage), then hard-clear every staff
 * 'medplum.*' localStorage key — including stale pre-cutover per-identity
 * prefixes (medplum.doctor./medplum.receptionist./…) that hold real tokens
 * from the auto-login era. Anonymous DEVICE sessions ('medplum.anon.*') are
 * deliberately kept: a staff logout must not disarm the waiting-room board or
 * kiosk credential on the same machine.
 */
export async function signOutMedplum() {
  currentUserCache = null;
  const medplum = await getClient();
  try {
    await medplum.signOut(); // POST auth/logout, then clears itself
  } catch (_) {
    // Server revocation failed (offline / rate-limited / already expired).
    // The LOCAL clear below is what actually logs this browser out — proceed.
  }
  // signOut() only clears in-memory auth state when the server POST succeeds
  // (verified live: a failed auth/logout left the session usable). clear() is
  // idempotent — always drop the in-memory login + the client's own storage.
  medplum.clear();
  for (const k of Object.keys(window.localStorage)) {
    if (k.startsWith('medplum.') && !k.startsWith('medplum.anon.')) window.localStorage.removeItem(k);
  }
}

/**
 * Bearer header for Express calls that survived the staff-session removal
 * (requireMedplumBearer routes). Reads THE staff session's access token
 * straight from its persisted storage ('medplum.user.activeLogin') — sync, no
 * client construction — so any fetch()/api() helper can spread it inline:
 * `headers: { ...authHeader() }`. Returns {} when there is no live session
 * (the server answers 401 and the app's NotLoggedInError path handles it).
 * MedplumClient writes every token refresh back to this same storage key, so
 * the value tracks the active session.
 */
export function authHeader() {
  let raw;
  try {
    raw = window.localStorage.getItem(STORAGE_PREFIX + 'activeLogin');
  } catch (_) {
    return {}; // storage unavailable (privacy mode) — same as "not logged in"
  }
  if (!raw) return {};
  try {
    const token = JSON.parse(raw).accessToken;
    return token ? { Authorization: 'Bearer ' + token } : {};
  } catch (_) {
    return {}; // corrupt entry — unauthenticated request fails loud with 401
  }
}

// ── currentUser derivation ──────────────────────────────────────────────────
// The static cfg.currentUser is gone; role/department/name are derived from what
// Medplum ACTUALLY returns for the live session:
//   role: the SOLE source is auth/me's top-level effective AccessPolicy.basedOn[].display
//     → the canonical opd-* role (ROLE_BY_POLICY_PREFIX). Exactly one canonical policy
//     must match; a missing or ambiguous effective policy THROWS — there is no
//     Practitioner-extension / membership-policy-name / seed-shape fallback chain any
//     more. Human admin/owner memberships are admin:false; only privileged Bots are
//     Medplum project administrators. The one exception is the bedside DEVICE identity,
//     which short-circuits to role 'bedside' off its profile identifier (its auth/me
//     summary carries no access[] to resolve a policy from — see BEDSIDE_DEVICE_ID_SYSTEM).
//     Practitioner.extension https://healther.io/opd/role is a roster/display mirror
//     only (opd-admin can edit Practitioner) and never participates in authorization here.
//   department: PractitionerRole.organization (seed doctors) or the
//     membership's department parameter (Bot-invited doctors/nurses), resolved
//     to the dept Organization → its department-slug identifier value.
const ROLE_BY_POLICY_PREFIX = [
  // 'opd-intern' MUST precede 'opd-doctor' is unnecessary (names are distinct), but
  // interns derive role 'intern' from opd-intern-rowlevel — a trainee/visiting tier
  // that works up patients but cannot prescribe/sign (owner decision 2026-07-19).
  ['opd-intern', 'intern'],
  ['opd-doctor', 'doctor'],
  ['opd-nurse', 'nurse'],
  ['opd-receptionist', 'receptionist'],
  ['opd-admin', 'admin'],
  ['opd-owner', 'owner'],
  // Dedicated bedside-device account (opd-bedside AccessPolicy, glance-only RLS).
  // A tablet signed in as this identity derives role 'bedside'.
  ['opd-bedside', 'bedside'],
];
// The dedicated bedside-device identity (provision-bedside-device.mjs) stamps this
// identifier system on its Practitioner profile. It is the RELIABLE, admin-free
// signal for role 'bedside': a bedside token's own auth/me summary omits
// membership.access[] entirely (verified live on Medplum 5.1.x — the non-admin
// membership read is project-admin-only), so the ROLE_BY_POLICY_PREFIX 'opd-bedside'
// mapping below can never fire for the device, and deriveRole's seed-shape fallback
// would otherwise mis-derive it as 'receptionist'. The profile identifier, by
// contrast, a user can always read on its own Practitioner (extensions/identifiers
// intact), so we key role derivation on it directly.
const BEDSIDE_DEVICE_ID_SYSTEM = 'https://healther.tech/bedside-device';
function isBedsideDevice(profile) {
  return profile?.resourceType === 'Practitioner'
    && ((profile.identifier || []).some((i) => i && i.system === BEDSIDE_DEVICE_ID_SYSTEM));
}

let currentUserCache = null; // { key: profile.id, promise } — per-session memo

/**
 * Derives { id, name, displayName, role, department } (the exact DTO shape
 * the old static cfg.currentUser had) from the live session. Cached per
 * profile; pass the session's medplum or let it resolve via getMedplum().
 * Throws NotLoggedInError when there is no live session.
 */
export function deriveCurrentUser(medplum) {
  const run = async () => {
    const client = medplum || (await getMedplum()).medplum;
    const profile = await client.getProfileAsync();
    if (!profile) throw new NotLoggedInError();
    if (!currentUserCache || currentUserCache.key !== profile.id) {
      const promise = computeCurrentUser(client, profile);
      currentUserCache = { key: profile.id, promise };
      // A failed derivation must not be memoized — the next call recomputes.
      promise.catch(() => {
        if (currentUserCache && currentUserCache.promise === promise) currentUserCache = null;
      });
    }
    return currentUserCache.promise;
  };
  return run();
}

async function computeCurrentUser(medplum, profile) {
  const membership = medplum.getProjectMembership(); // summary only; protected fields may be stripped
  const effectiveAccessPolicy = medplum.getAccessPolicy(); // auth/me server-issued effective policy
  const n = (profile.name && profile.name[0]) || undefined;
  const name = (n && (n.text || [...(n.given || []), n.family].filter(Boolean).join(' '))) || '';

  // Every staff policy grants PractitionerRole read (no criteria) — a failure
  // here is a real policy regression and should surface, not be swallowed.
  let practRole;
  if (profile.resourceType === 'Practitioner') {
    practRole = await medplum.searchOne('PractitionerRole',
      'practitioner=Practitioner/' + profile.id + '&active=true');
  }

  // Bedside device short-circuits to role 'bedside' off its profile identifier
  // (see BEDSIDE_DEVICE_ID_SYSTEM) — its auth/me summary carries no access[] to
  // resolve the policy name from, so the prefix mapping below can't reach it.
  const role = isBedsideDevice(profile)
    ? 'bedside'
    : deriveRole(effectiveAccessPolicy);
  // The device has no department Organization (project-global glance) — skip the
  // dept resolution (which would 403 on the excluded reads anyway) and report null.
  const department = role === 'bedside' ? null : await deriveDepartment(medplum, membership, practRole);
  // Seniority (jr-resident | sr-resident | associate-prof | hod) off the caller's
  // OWN PractitionerRole.code (the admin-staff-invite Bot writes it there). isHOD is
  // the boolean the Protocols management UI gates on. Both are '' / false for staff
  // without a seniority coding (every non-HOD doctor today) — additive, never blocks.
  const { seniority, isHOD } = deriveSeniority(practRole);
  // The FINE 8-tier designation (band + optional grade) off the SAME PractitionerRole,
  // as { band, grade, key, short, label, rank } — or null when the role carries no
  // seniority band (admin/reception, or a doctor with no designation assigned yet).
  // The avatar menu renders `short` as a chip and `label` as its tooltip.
  const designation = deriveDesignation(practRole);
  // The department Organization reference (Organization/<uuid>) — the management UI
  // needs the REF (not just the slug) to stamp a new protocol's owning-department.
  const departmentRef = deriveDepartmentRef(membership, practRole);
  // The forced-password-change marker: read it off the caller's OWN Practitioner
  // profile (in hand from auth/me — a user can always read its own profile, on
  // any server version, extensions intact). The invite Bot mirrors the marker
  // there for exactly this reason (the ProjectMembership copy is admin-only and
  // invisible to the non-admin who must act on it). The membership copy is still
  // honoured as a fallback for admin logins (which CAN read their membership),
  // so an admin invited with a temp password is forced to change too.
  const hasMarker = (res) => ((res && res.extension) || []).some(
    (e) => e.url === MUST_CHANGE_PWD_EXT && e.valueBoolean === true,
  );
  const mustChangePassword = hasMarker(profile) || hasMarker(membership);
  // Doctor honorific for display parity with the old static DTO
  // ('Dr Asha Verma'); the Practitioner name itself carries no prefix.
  const displayName = role === 'doctor' && name && !/^dr\.?\s/i.test(name) ? 'Dr ' + name : name;
  return { id: profile.id, name, displayName, role, department, departmentRef, seniority, isHOD, designation, mustChangePassword };
}

// Seniority off the caller's own PractitionerRole.code seniority coding. Returns
// { seniority:'', isHOD:false } when absent (the common case today). We read the
// coding the admin-staff-invite Bot writes (system SENIORITY_SYSTEM); code 'hod'
// is the only one the protocols surface treats specially.
function deriveSeniority(practRole) {
  for (const cc of (practRole && practRole.code) || []) {
    for (const c of (cc.coding || [])) {
      if (c.system === SENIORITY_SYSTEM && c.code) {
        const code = String(c.code).toLowerCase();
        return { seniority: code, isHOD: code === 'hod' };
      }
    }
  }
  return { seniority: '', isHOD: false };
}

// The FINE 8-tier designation off the caller's own PractitionerRole.code — the
// coarse band coding (SENIORITY_SYSTEM) plus, for the JR/SR bands, the sibling
// grade coding (SENIORITY_GRADE_SYSTEM). Returns { band, grade, key, short, label,
// rank } or null when no band is present.
//
// INLINED on purpose: this is a compact copy of designationOfRole()/parseDesignation()
// in medplum-designation.js. That module imports getMedplum() from THIS module, so
// importing its reader back here would create an import cycle (forbidden — AGENTS §1).
// The two must agree; the designation vitest covers the tier→short/label mapping.
const DESIGNATION_BAND_BASE = { 'jr-resident': 10, 'sr-resident': 20, 'associate-prof': 30, hod: 40 };
function normDesignationBand(v) {
  const t = String(v || '').trim().toLowerCase();
  if (t === 'jr' || t === 'jr-resident' || t === 'junior' || t === 'junior-resident') return 'jr-resident';
  if (t === 'sr' || t === 'sr-resident' || t === 'senior' || t === 'senior-resident') return 'sr-resident';
  if (t === 'associate-prof' || t === 'associate' || t === 'assoc' || t === 'associate-professor' || t === 'ap') return 'associate-prof';
  if (t === 'hod' || t === 'head' || t === 'head-of-department') return 'hod';
  return '';
}
function normDesignationGrade(v) {
  const n = parseInt(String(v == null ? '' : v).trim(), 10);
  return (n === 1 || n === 2 || n === 3) ? n : 0;
}
function deriveDesignation(practRole) {
  let bandCode = '';
  let gradeCode = 0;
  for (const cc of (practRole && practRole.code) || []) {
    for (const c of (cc.coding || [])) {
      if (c && c.system === SENIORITY_SYSTEM && c.code) bandCode = String(c.code).toLowerCase();
      if (c && c.system === SENIORITY_GRADE_SYSTEM && c.code) gradeCode = normDesignationGrade(c.code) || gradeCode;
    }
  }
  const band = normDesignationBand(bandCode);
  if (!band) return null;
  let grade = normDesignationGrade(gradeCode);
  if (band === 'associate-prof' || band === 'hod') grade = 0;
  const short = band === 'jr-resident' ? (grade ? `JR${grade}` : 'JR')
    : band === 'sr-resident' ? (grade ? `SR${grade}` : 'SR')
      : band === 'associate-prof' ? 'AP' : 'HOD';
  const label = band === 'jr-resident' ? `Junior Resident${grade ? ` ${grade}` : ''}`
    : band === 'sr-resident' ? `Senior Resident${grade ? ` ${grade}` : ''}`
      : band === 'associate-prof' ? 'Associate Professor' : 'Head of Department';
  const key = (band === 'jr-resident' || band === 'sr-resident') && grade
    ? (band === 'jr-resident' ? 'jr' : 'sr') + grade : band;
  const rank = DESIGNATION_BAND_BASE[band] + grade;
  return { band, grade, rank, key, short, label };
}

// The doctor's department Organization reference (Organization/<uuid>) — from the
// PractitionerRole.organization (seed doctors) or the membership's department
// parameter (Bot-invited doctors). Same resolution as deriveDepartment, but returns
// the REF the management UI stamps as owning-department, not the resolved slug.
function deriveDepartmentRef(membership, practRole) {
  const orgRef = (practRole && practRole.organization && practRole.organization.reference) || '';
  if (orgRef) return orgRef;
  for (const a of (membership && membership.access) || []) {
    const p = (a.parameter || []).find((x) =>
      x.name === 'department' && x.valueReference && x.valueReference.reference);
    if (p) return p.valueReference.reference;
  }
  return null;
}

function deriveRole(effectiveAccessPolicy) {
  const policyNames = (effectiveAccessPolicy?.basedOn || []).map((ref) => ref?.display).filter(Boolean);
  const policyRoles = new Set();
  for (const policyName of policyNames) {
    const normalized = String(policyName).trim().toLowerCase();
    for (const [prefix, role] of ROLE_BY_POLICY_PREFIX) {
      if (normalized === prefix || normalized.startsWith(prefix + '-')) policyRoles.add(role);
    }
  }
  if (policyRoles.size === 1) {
    return [...policyRoles][0];
  }
  if (policyRoles.size > 1) throw new Error('multiple canonical staff policies are active for this account');
  throw new Error('staff session is missing its canonical effective AccessPolicy role');
}

async function deriveDepartment(medplum, membership, practRole) {
  let orgRef = (practRole && practRole.organization && practRole.organization.reference) || '';
  if (!orgRef) {
    for (const a of (membership && membership.access) || []) {
      const p = (a.parameter || []).find((x) =>
        x.name === 'department' && x.valueReference && x.valueReference.reference);
      if (p) { orgRef = p.valueReference.reference; break; }
    }
  }
  if (!orgRef) return null; // receptionist/admin: project-global, no department
  const org = await medplum.readReference({ reference: orgRef });
  const slug = (org.identifier || []).find((i) => i.system === DEPT_SLUG_SYSTEM);
  return (slug && slug.value) || (org.name ? org.name.toLowerCase() : null);
}

// ── Kiosk DEVICE session ─────────────────────────────────────────────────────
// The reception kiosk is a PER-TABLET device credential provisioned by scripts/medplum/provision-kiosk-
// device.mjs and armed ONCE per physical tablet by staff. Its {clientId,
// clientSecret} are therefore stored locally (under medplum.anon.kioskDevice.*,
// inside the same anon firewall — signOutMedplum preserves medplum.anon.*), not
// in the shipped config. Bound to the opd-kiosk-device AccessPolicy: create-only
// intake writes + a hiddenFields-narrowed Patient demographics read, no chart.
const KIOSK_DEVICE_PREFIX = 'medplum.anon.kioskDevice.';
const KIOSK_CRED_KEY = KIOSK_DEVICE_PREFIX + 'cred';
const kioskDeviceSessions = new Map();

// One-time staff arming step. Validates the pair by actually logging in
// (startClientLogin); only persists on success so a mistyped secret never
// silently "arms" a dead kiosk. Returns the device profile for confirmation.
export async function armKioskDevice({ clientId, clientSecret } = {}) {
  if (!clientId || !clientSecret) throw new Error('armKioskDevice requires { clientId, clientSecret }');
  const cfg = await loadConfig();
  const medplum = new MedplumClient({ baseUrl: cfg.baseUrl, storage: new PrefixedStorage(KIOSK_DEVICE_PREFIX) });
  await medplum.startClientLogin(clientId, clientSecret); // throws on bad creds → not stored
  const profile = await medplum.getProfileAsync();
  // Persist the pair only after a proven login, so getMedplumKioskDevice can
  // re-authenticate on later boots without a fresh arming step.
  window.localStorage.setItem(KIOSK_CRED_KEY, JSON.stringify({ clientId, clientSecret }));
  kioskDeviceSessions.set('kioskDevice', Promise.resolve({ medplum, cfg, hospital: cfg.hospital }));
  return profile;
}

export function isKioskDeviceArmed() {
  return !!window.localStorage.getItem(KIOSK_CRED_KEY);
}

async function initKioskDeviceSession() {
  const raw = window.localStorage.getItem(KIOSK_CRED_KEY);
  if (!raw) throw new Error('kiosk device is not armed — run the staff arming step (armKioskDevice)');
  const { clientId, clientSecret } = JSON.parse(raw);
  const cfg = await loadConfig();
  const medplum = new MedplumClient({ baseUrl: cfg.baseUrl, storage: new PrefixedStorage(KIOSK_DEVICE_PREFIX) });
  if (!(await medplum.getProfileAsync())) {
    await medplum.startClientLogin(clientId, clientSecret);
  }
  return { medplum, cfg, hospital: cfg.hospital };
}

// Returns { medplum, cfg, hospital } for the armed kiosk device. Cached like
// staff singleton. Throws if the tablet has not been armed.
export function getMedplumKioskDevice() {
  if (!kioskDeviceSessions.has('kioskDevice')) {
    kioskDeviceSessions.set('kioskDevice', initKioskDeviceSession().catch((e) => { kioskDeviceSessions.delete('kioskDevice'); throw e; }));
  }
  return kioskDeviceSessions.get('kioskDevice');
}
